/**
 * Source-type registry — declarative metadata per TrafficSourceKind. Drives
 * the "New traffic source" picker, the default click/postback URL templates,
 * and the upstream→trackingField mapping seed.
 *
 * The metadata is generated from the Go registry at build time. The Go test
 * `TestFESnapshotInSync` in internal/sourcetype/snapshot_test.go asserts that
 * source-types.generated.json matches the Go-side `sourcetype.Export()` output
 * — drift on either side fails CI. To regenerate after an intentional change:
 *
 *   UPDATE_SOURCETYPE_SNAPSHOT=1 go test ./internal/sourcetype/...
 *
 * Hand-written augmentations (FE-only concerns: icons, accent colors, sort
 * order) live in the SOURCE_PRESENTATION map below.
 */
import { TrafficSourceKind } from "@gen/tracker/v1/traffic_source_pb";
import rawSpecs from "./source-types.generated.json";

/**
 * One query parameter the buyer pastes into the ad platform's own field.
 * `param` is what the tracker edge reads (sub1..sub20 or the click-id param);
 * `macro` is the PLATFORM's spelling, expanded per impression by Meta/Google/
 * TikTok — never a tracker macro from lib/macros.ts.
 */
export interface AdParam {
  param: string;
  macro: string;
  label: string;
  default: boolean;
}

/**
 * One field the buyer fills before he is handed anything, exactly as the Go
 * spec declares it. The MESSAGES are data and not UI copy on purpose: a pixel
 * is wrong in a platform-specific way, and «обязательное поле» teaches nobody
 * where to look. Mirrors sourcetype.AskField.
 */
export interface AskField {
  key: string;
  /** Name the server reports this field under via X-Field. */
  serverField: string;
  label: string;
  /** Also the canonical valid example — the Go test holds it to the rule. */
  placeholder: string;
  /** Anchored RE2 from the spec; empty means "any non-empty value". */
  pattern: string;
  required: boolean;
  requiredError: string;
  invalidError: string;
  /** What people paste instead. The Go test asserts the rule rejects it. */
  commonMistake: string;
}

/**
 * One value the buyer copies, and the field it goes into on the platform's
 * side. Facebook declares two of these, Google two, TikTok one — which is why
 * the dialog renders a row per entry and never assumes a count.
 * Mirrors sourcetype.GiveField.
 */
export interface GiveField {
  key: string;
  label: string;
  /** The input in the ad account, in the platform's own words. */
  adField: string;
  template: string;
  hint: string;
}

/** Raw shape emitted by the Go registry. Matches sourcetype.ExportedSpec. */
export interface SourceTypeSpec {
  kind: TrafficSourceKindString;
  displayName: string;
  description: string;
  clickIdParam: string;
  clickIdColumn: string;
  cookieName: string;
  cookieMaxAgeSecs: number;
  defaultClickUrl: string;
  defaultMacros: string[];
  defaultTrackingMap: Record<string, string>;
  adParams: AdParam[];
  adParamsHint: string;
  pixelParam: string;
  askFields: AskField[];
  giveFields: GiveField[];
}

export type TrafficSourceKindString =
  | "FACEBOOK_ADS"
  | "GOOGLE_ADS"
  | "TIKTOK_ADS"
  | "NATIVE"
  | "PUSH"
  | "SEARCH"
  | "SEO"
  | "EMAIL"
  | "DIRECT"
  | "CUSTOM";

/** FE-only presentation (not part of the BE contract). */
export interface SourcePresentation {
  iconKey: string;
  accentToken?: string;
  /** Picker grid sort priority (lower = first). */
  order: number;
  /**
   * The platform as a buyer says it («Ссылка для Facebook», доски Campaign и
   * LinkReady) — shorter than the registry's product name «Facebook Ads».
   */
  brand?: string;
}

export const SOURCE_PRESENTATION: Record<TrafficSourceKindString, SourcePresentation> = {
  FACEBOOK_ADS: { iconKey: "facebook", accentToken: "--source-facebook", order: 0, brand: "Facebook" },
  TIKTOK_ADS:   { iconKey: "tiktok",   accentToken: "--source-tiktok",   order: 1, brand: "TikTok" },
  GOOGLE_ADS:   { iconKey: "google",   accentToken: "--source-google",   order: 2, brand: "Google" },
  NATIVE:       { iconKey: "native",                                     order: 3 },
  PUSH:         { iconKey: "push",                                       order: 4 },
  SEARCH:       { iconKey: "search",                                     order: 5 },
  SEO:          { iconKey: "seo",                                        order: 6 },
  EMAIL:        { iconKey: "email",                                      order: 7 },
  DIRECT:       { iconKey: "direct",                                     order: 8 },
  CUSTOM:       { iconKey: "custom",                                     order: 9 },
};

/** Picker-ordered list of every registered source. */
export const SOURCE_TYPES: SourceTypeSpec[] = (rawSpecs as unknown as SourceTypeSpec[])
  .slice()
  .sort((a, b) => SOURCE_PRESENTATION[a.kind].order - SOURCE_PRESENTATION[b.kind].order);

const BY_KIND: Record<TrafficSourceKindString, SourceTypeSpec> = Object.fromEntries(
  SOURCE_TYPES.map((s) => [s.kind, s]),
) as Record<TrafficSourceKindString, SourceTypeSpec>;

export function getSourceType(kind: TrafficSourceKindString): SourceTypeSpec {
  return BY_KIND[kind] ?? BY_KIND.CUSTOM;
}

export function sourcePresentation(kind: TrafficSourceKindString): SourcePresentation {
  return SOURCE_PRESENTATION[kind] ?? SOURCE_PRESENTATION.CUSTOM;
}

/** Whether the source carries a first-party click ID (fbclid, ttclid, gclid). */
export function hasClickId(kind: TrafficSourceKindString): boolean {
  return getSourceType(kind).clickIdParam !== "";
}

/** The ad-platform macro palette for a source; empty for ingestion-only kinds. */
export function adParams(kind: TrafficSourceKindString): AdParam[] {
  return getSourceType(kind).adParams ?? [];
}

/**
 * The parameters a rendered ad link carries: the palette's defaults, the ids a
 * buyer optimizes on. Mirrors sourcetype.Spec.LinkParams — the server renders
 * a launcher's link (CampaignService.RenderAdLink) from the same set, so the
 * dialog and the launcher can never hand out two different strings.
 */
export function linkParams(kind: TrafficSourceKindString): AdParam[] {
  return adParams(kind).filter((p) => p.default);
}

/**
 * Joins params into the string an ad platform's "URL parameters" field wants:
 * no leading "?" — Meta rejects that.
 */
export function adParamString(params: AdParam[]): string {
  return params.map((p) => `${p.param}=${p.macro}`).join("&");
}

/** Appends a parameter string to a URL, respecting an existing query. */
export function withAdParams(url: string, params: AdParam[]): string {
  const qs = adParamString(params);
  if (!url || !qs) return url;
  return url + (url.includes("?") ? "&" : "?") + qs;
}

/* ── the ad-link template: what we ask, what we give ──────────────────── */

/** The fields this platform's link is invalid without. */
export function askFields(kind: TrafficSourceKindString): AskField[] {
  return getSourceType(kind).askFields ?? [];
}

/** The values the buyer copies. One entry per field in his ad account. */
export function giveFields(kind: TrafficSourceKindString): GiveField[] {
  return getSourceType(kind).giveFields ?? [];
}

/** Whether this platform has an ad account to paste into at all. */
export function hasAdLinkTemplate(kind: TrafficSourceKindString): boolean {
  const s = getSourceType(kind);
  return (s.askFields?.length ?? 0) > 0 && (s.giveFields?.length ?? 0) > 0;
}

/**
 * Runs one ask rule. Returns the spec's own message, or "" when the value is
 * acceptable — the caller never composes a message of its own, because the
 * message is the part that differs per platform.
 */
export function validateAskField(field: AskField, raw: string): string {
  const value = raw.trim();
  if (!value) return field.required ? field.requiredError : "";
  if (!field.pattern) return "";
  // The pattern is authored anchored in Go; a malformed one is a spec bug and
  // must not take the dialog down with it, so a failed compile reads as "no
  // rule" rather than as "invalid".
  try {
    return new RegExp(field.pattern).test(value) ? "" : field.invalidError;
  } catch {
    return "";
  }
}

/** Every failing ask field, keyed by ask key. Empty object = ready to give. */
export function validateAsk(
  kind: TrafficSourceKindString,
  values: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of askFields(kind)) {
    const msg = validateAskField(f, values[f.key] ?? "");
    if (msg) out[f.key] = msg;
  }
  return out;
}

/**
 * Expands a give template. The placeholder alphabet is `%{link}`, `%{params}`
 * and `%{ask.KEY}` — deliberately NOT `{…}`, because the strings being built
 * are full of braces that belong to the platforms: Google's tracking template
 * literally contains `{lpurl}`, Meta writes `{{campaign.id}}`. Kept in
 * lockstep with sourcetype.RenderGive.
 */
const GIVE_TOKEN = /%\{(link|params|ask\.[a-z0-9_]+)\}/g;

export function renderGive(
  give: GiveField,
  link: string,
  params: string,
  ask: Record<string, string>,
): string {
  return give.template.replace(GIVE_TOKEN, (_whole, name: string) => {
    if (name === "link") return link;
    if (name === "params") return params;
    return (ask[name.slice("ask.".length)] ?? "").trim();
  });
}

/* ── proto ↔ string bijection ──────────────────────────────────────────── */

const KIND_TO_PROTO: Record<TrafficSourceKindString, TrafficSourceKind> = {
  FACEBOOK_ADS: TrafficSourceKind.FACEBOOK_ADS,
  GOOGLE_ADS:   TrafficSourceKind.GOOGLE_ADS,
  TIKTOK_ADS:   TrafficSourceKind.TIKTOK_ADS,
  NATIVE:       TrafficSourceKind.NATIVE,
  PUSH:         TrafficSourceKind.PUSH,
  SEARCH:       TrafficSourceKind.SEARCH,
  SEO:          TrafficSourceKind.SEO,
  EMAIL:        TrafficSourceKind.EMAIL,
  DIRECT:       TrafficSourceKind.DIRECT,
  CUSTOM:       TrafficSourceKind.CUSTOM,
};

const PROTO_TO_KIND: Record<number, TrafficSourceKindString> = Object.fromEntries(
  (Object.entries(KIND_TO_PROTO) as [TrafficSourceKindString, TrafficSourceKind][]).map(
    ([k, p]) => [p, k],
  ),
) as Record<number, TrafficSourceKindString>;

export function kindToProto(k: TrafficSourceKindString): TrafficSourceKind {
  return KIND_TO_PROTO[k] ?? TrafficSourceKind.CUSTOM;
}

export function kindFromProto(p: TrafficSourceKind): TrafficSourceKindString {
  return PROTO_TO_KIND[p] ?? "CUSTOM";
}

/** The platform in a buyer's words: «Facebook», else the registry's name. */
export function trafficSourceBrand(k: TrafficSourceKind | TrafficSourceKindString): string {
  const str = typeof k === "string" ? k : kindFromProto(k);
  return sourcePresentation(str).brand ?? getSourceType(str).displayName;
}

export function trafficSourceKindLabel(k: TrafficSourceKind | TrafficSourceKindString): string {
  const str = typeof k === "string" ? k : kindFromProto(k);
  return getSourceType(str).displayName;
}

/* ── validation helpers shared with the form ──────────────────────────── */

/** Tracking-field-map values MUST match trackingField1..20. */
const TRACKING_SLOT_RE = /^trackingField(?:[1-9]|1[0-9]|20)$/;

/** Macro keys (tracking_field_map keys, cost_macros) follow the same shape. */
const MACRO_KEY_RE = /^[a-zA-Z0-9_.\-]+$/;

export function isValidTrackingSlot(slot: string): boolean {
  return TRACKING_SLOT_RE.test(slot);
}

export function isValidMacroKey(key: string): boolean {
  return MACRO_KEY_RE.test(key);
}

export const TRACKING_FIELD_SLOTS: string[] = Array.from(
  { length: 20 },
  (_, i) => `trackingField${i + 1}`,
);
